<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import LocalizedInput from '@/components/LocalizedInput.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import BaseDateInput from '@/components/base/BaseDateInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';

import type { Locales, ProjectModuleNewsItemConfigDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { computed, toRef } from 'vue';
import { formSaveImageGuard } from '@/helpers/form.helper';
import { useNewsItemsStore } from '@/stores/newsItems.store';
import { useIsFormDirty } from 'vee-validate';
import { useTheme } from '@/composables/useTheme';
import { ModuleTypeEnum } from '@/types/modules';

// CONST
const { t } = useI18n();
const newsItemsStore = useNewsItemsStore();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleNewsItemConfigDto;
  themeKey: string;
}>();

const emit = defineEmits<{
  (e: 'saveImage', options: SavingOptions): void;
  (e: 'deleteImage'): void;
}>();

// COMPUTED
const file = computed<FileInfo | null>(() => (props.module.config?.image as FileInfo) ?? null);
const statusOptions = computed(() => newsItemsStore.getStatuses());
const isDirty = useIsFormDirty();
const themeConfiguration = useTheme('project', toRef(props, 'themeKey'));
const themeCropping = themeConfiguration.getCroppingInfo(ModuleTypeEnum.News, 1);
const cropping = computed(() => themeCropping.value);

// FUNCTIONS
const handleSaveImage = async (options: SavingOptions) => {
  emit('saveImage', options);
};

function removeImage() {
  emit('deleteImage');
}

const handleClearImage = async () => {
  formSaveImageGuard(false, removeImage);
};
</script>
<template>
  <div class="module-news-item">
    <localized-input
      name="title"
      :label="t('global.label.title')"
      :editLocale="editLocale"
      outlined
      :language_enabled="props.languageEnabled"
    />
    <q-field :label="t('global.label.description')" stack-label borderless>
      <template v-slot:control>
        <localized-editor
          name="description"
          :label="t('global.label.content')"
          class="q-mt-sm full-width"
          :editLocale="props.editLocale"
          :language_enabled="languageEnabled"
        />
      </template>
    </q-field>
    <q-field :label="t('global.label.content')" stack-label borderless>
      <template v-slot:control>
        <localized-editor
          name="config.content"
          :label="t('global.label.content')"
          class="q-mt-sm full-width"
          :editLocale="props.editLocale"
          :language_enabled="languageEnabled"
        />
      </template>
    </q-field>

    <base-date-input name="date" :label="t('project-module.news.item.date')" />

    <image-field
      :label="t('form.question.image.label')"
      version="v2"
      :cropping="cropping"
      :file="file"
      @clear-image="handleClearImage"
      @save-image="handleSaveImage"
      :form-dirty="isDirty"
      :language-enabled="props.languageEnabled"
      alt-text-name="config.image.metadata.image.altText"
    />

    <localized-select
      name="status"
      :label="t('project-module.news.item.status')"
      :editLocale="editLocale"
      :options="statusOptions"
      emit-value
      map-options
      outlined
    />
  </div>
</template>
